import { DatePicker, DatePickerProps } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { RangePickerProps } from 'antd/es/date-picker'

type CaregiverDatePickerValue = Dayjs | null

type CaregiverDatePickerProps = DatePickerProps & {
  timezone: string
}

export const getCaregiverDisplayFormat = (timezone: string, format?: string): string => {
  // HACK (Jason): we can't use the 'z' format for timezone because it will display the timezone
  // of dayjs only occasionally, so we have to manually stub in the timezone and display it.
  const finalTimezone = dayjs().tz(timezone).format('z')
  const formatWithoutTimezone = format?.replace('z', '')

  return `${formatWithoutTimezone ?? 'MM-DD-YYYY'} [${finalTimezone}]`
}

export const formatDateInCaregiverTimezone = (
  value: string | Dayjs | null | undefined,
  timezone: string,
  format = 'MM-DD-YYYY'
): string | null => {
  if (!value) return null

  const parsedDate = dayjs.isDayjs(value) ? value : dayjs(value)
  if (!parsedDate.isValid()) {
    return typeof value === 'string' ? value : null
  }

  return parsedDate.tz(timezone).format(getCaregiverDisplayFormat(timezone, format))
}

export const CaregiverDatePicker: React.FC<CaregiverDatePickerProps> = (datePickerProps) => {
  const { value, onChange, format, timezone, ...props } = datePickerProps

  const hackyFormat = getCaregiverDisplayFormat(timezone, format?.toString())

  // HACK (Jason): DatePicker.RangePicker insists upon displaying the time in the local timezone,
  // so we have to make a few hacks to display the time in the caregiver's timezone.
  const valueInCaregiverTimezone = value ? value.tz(timezone) : null
  // This has to be done so it displays as the right time in the time picker.
  const valueCastedToLocalTimezone = valueInCaregiverTimezone
    ? valueInCaregiverTimezone.tz(dayjs.tz.guess(), true)
    : null

  const transformTime = (timeInLocal: CaregiverDatePickerValue): CaregiverDatePickerValue => {
    // Times from time picker comes in local time so before saving we must:
    // 1. Cast it to the caregiver's timezone
    // 2. Convert it to UTC
    const timeCastedToCaregiverTimezone = timeInLocal ? timeInLocal.tz(timezone, true) : null
    const timeInUtc = timeCastedToCaregiverTimezone ? timeCastedToCaregiverTimezone.utc() : null

    return timeInUtc
  }

  return (
    <DatePicker
      style={{ width: '100%' }}
      {...props}
      format={hackyFormat}
      value={valueCastedToLocalTimezone}
      onChange={(time, ...onChangeParams) =>
        onChange ? onChange(transformTime(time), ...onChangeParams) : undefined
      }
    />
  )
}

type CaregiverDateRangePickerValue = [CaregiverDatePickerValue, CaregiverDatePickerValue] | null

type CaregiverDateRangePickerProps = RangePickerProps & {
  timezone: string
}

export const CaregiverDateRangePicker: React.FC<CaregiverDateRangePickerProps> = (
  DateRangePickerProps
) => {
  const { value: rangeValue, format, onChange, timezone, ...props } = DateRangePickerProps

  const hackyFormat = getCaregiverDisplayFormat(timezone, format?.toString())

  const rangeValueCastedToLocalTimezone =
    rangeValue && Array.isArray(rangeValue)
      ? (rangeValue.map((value) => {
          const valueInCaregiverTimezone = value ? value.tz(timezone) : null
          // This has to be done so it displays as the right time in the time picker.
          const valueCastedToLocalTimezone = valueInCaregiverTimezone
            ? valueInCaregiverTimezone.tz(dayjs.tz.guess(), true)
            : null

          return valueCastedToLocalTimezone
        }) as [CaregiverDatePickerValue, CaregiverDatePickerValue])
      : undefined

  const transformRange = (
    rangeInLocal: CaregiverDateRangePickerValue
  ): CaregiverDateRangePickerValue => {
    // Convert to UTC to eject from daylight savings, and then switch to today and then
    // display times.
    const rangeInUtc =
      rangeInLocal && Array.isArray(rangeInLocal)
        ? (rangeInLocal.map((timeInLocalTimezone): CaregiverDatePickerValue => {
            // Times from time range picker comes in local time so before saving we must:
            // 1. Cast it to the caregiver's timezone
            // 2. Convert it to UTC
            const timeCastedToCaregiverTimezone = timeInLocalTimezone
              ? timeInLocalTimezone.tz(timezone, true)
              : null
            const timeInUtc = timeCastedToCaregiverTimezone
              ? timeCastedToCaregiverTimezone.utc()
              : null

            return timeInUtc
          }) as CaregiverDateRangePickerValue)
        : null

    return rangeInUtc
  }

  return (
    <DatePicker.RangePicker
      {...props}
      format={hackyFormat}
      value={rangeValueCastedToLocalTimezone}
      onChange={(range, ...onChangeParams) =>
        onChange ? onChange(transformRange(range), ...onChangeParams) : undefined
      }
    />
  )
}
